import { useQuery } from '@tanstack/react-query';
import { Pill, ErrorPanel, Refused } from '@cwos/ui-admin';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { usePermissions } from '../permissions.js';
import { forecasts, type ForecastSummary } from './forecastsClient.js';
import {
  emptyLine,
  forecastSentence,
  likelihoodLabel,
  outcomeLabel,
  scopeLine,
  toneFor,
  withheldLine,
} from './forecastLogic.js';

/**
 * INTELLIGENCE → INSIGHTS → **ANTICIPATE**. What Phibian expects.
 *
 * ===========================================================================
 * WHY IT IS A SEPARATE PANEL AND NOT A ROW IN THE FINDINGS TABLE
 * ===========================================================================
 * Because a forecast is a different kind of claim, and putting the two in one
 * list would say they are not. Everything in "What Phibian noticed" is true
 * now. Everything here is about a day that has not arrived and MAY NOT ARRIVE
 * — that is the whole point of showing it early. A reader who cannot tell the
 * two apart at a glance will treat the predictions as facts, act on them with
 * the same urgency, and eventually discount both.
 *
 * ===========================================================================
 * EVERY ROW CARRIES ITS OWN UNCERTAINTY AND ITS OWN BASIS
 * ===========================================================================
 * Not a percentage — a WORD, plus the sentence the server derived it from,
 * plus the action that would prove it wrong. `FORECAST_MODEL` in the
 * organization's own operational library ends with "do not present unsupported
 * precision or confidence; disclose material missing data and assumptions",
 * and it was written before any forecaster existed. `undetermined` renders as
 * "We cannot say" and stays there; nothing on this screen rounds it up.
 *
 * ===========================================================================
 * AND NO RED
 * ===========================================================================
 * `toneFor` cannot return `urgent`. A wall of red about things that have not
 * happened is the fastest way to teach somebody to ignore a queue, and the
 * founder's tone rule forbids it for conditions that HAVE happened.
 */

/** The reading key, and the one the server's guard is argued from. */
const INSIGHTS_READ_KEY = 'insights:read';

/**
 * How many live forecasts to show. Small on purpose: this is a heads-up, not a
 * work queue. What is due in the next two months and cannot be prevented by
 * doing nothing fits in a short list, and a long one would be a wall.
 */
const PANEL_LIMIT = 10;

export function ForecastPanel() {
  const {
    has,
    isLoading: permissionsLoading,
    isError: permissionsError,
    refetch: refetchPermissions,
  } = usePermissions();

  /**
   * UNKNOWN IS NOT DENIED, and a FAILED permission read is its own state.
   * Folding the two together is how a panel spins forever: `isError` does not
   * clear on its own, so "not yet known" quietly becomes "never known".
   */
  const mayRead: boolean | undefined = permissionsLoading ? undefined : has(INSIGHTS_READ_KEY);

  const list = useQuery({
    queryKey: ['insights', 'forecasts', 'pending'],
    queryFn: () => forecasts.list({ outcome: 'pending', limit: PANEL_LIMIT }),
    enabled: mayRead === true && !permissionsError,
  });

  const items = list.data?.items ?? [];
  // READ OFF THE RESPONSE. The server said which population it answered about;
  // re-deriving it from the reader's own permissions is how a caseload answer
  // comes to be labelled organization-wide.
  const scope = list.data?.scope;
  const withheld = withheldLine(list.data?.withheld);

  return (
    <section className="rn-card" aria-labelledby="forecast-panel">
      <div className="rn-card-head">
        <h2 className="rn-card-title" id="forecast-panel">
          What Phibian expects
        </h2>
      </div>

      {permissionsError ? (
        <ErrorPanel
          message="We could not check whether you can see what Phibian expects. This is not a refusal — we do not know yet."
          onRetry={() => refetchPermissions()}
        />
      ) : mayRead === false ? (
        <Refused
          heading="What Phibian expects is not shown to you"
          message="This panel is withheld, not empty. Nothing has been left out by mistake."
        />
      ) : (
        <>
          <p className="rn-muted">
            Things that have not happened yet. {scopeLine(scope)} Each one says what would stop it.
          </p>
          <ListState
            phase={mayRead === undefined ? { kind: 'loading' } : listPhase(list, items.length)}
            surface="table"
            rows={2}
            failedMessage="We could not load what Phibian expects. Nothing here has changed."
            onRetry={() => void list.refetch()}
            refused={
              <Refused
                heading="What Phibian expects is not shown to you"
                message="This panel is withheld, not empty. Nothing has been left out by mistake."
              />
            }
            empty={<p className="rn-muted">{emptyLine(scope)}</p>}
          >
            <ul className="rn-list">
              {items.map((row) => (
                <ForecastRow key={row.id} row={row} />
              ))}
            </ul>
          </ListState>
          {/* WITHHELD IS NOT ZERO, and it is said rather than implied. */}
          {withheld ? <p className="rn-muted">{withheld}</p> : null}
        </>
      )}
    </section>
  );
}

function ForecastRow({ row }: { row: ForecastSummary }) {
  const outcome = outcomeLabel(row.outcome);
  return (
    <li className="rn-col" style={{ gap: 'var(--space-8)' }}>
      <div className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'center' }}>
        <span className="rn-strong">{forecastSentence(row)}</span>
        <Pill variant={toneFor(row.likelihood)}>{likelihoodLabel(row.likelihood)}</Pill>
        {outcome ? <span className="rn-muted">{outcome}</span> : null}
      </div>
      {/* THE BASIS, always. A prediction without one is an assertion. */}
      <span className="rn-muted">{row.likelihoodBasis}</span>
      {/* AND WHAT WOULD MAKE IT WRONG — the thing the reader is being asked to
          do, and the reason this is not a verdict. */}
      <span className="rn-muted">
        <strong>What would change this:</strong> {row.falsifiedBy}
      </span>
      <span className="rn-muted" style={{ fontSize: 13 }}>
        Expected on {row.horizonOn} · first noted {row.observedOn}
      </span>
    </li>
  );
}
